import { fromString, type MonetaryValue } from 'ezmoney';
import { useQuery, type UseQueryResult } from 'react-query';

import { getV2ApiCompanyIdBudgetDefinitionsBudgetDefinitionIdBreakdowns } from 'src/client/spendesk-api';
import { useCompany } from 'src/core/modules/app/hooks/useCompany';

import { getApiKey } from './keys';
import { type PeriodInterval } from '../../models/periodInterval';
import { type BranchOf, type BudgetTree } from '../models/budgetTree';
import { buildApiPeriod, buildPeriod } from '../models/period';

type BreakdownSplit<T = MonetaryValue> = {
  committed: T;
  used: T;
};

export type Breakdown<T = MonetaryValue> = {
  allocated: T;
  remaining: T;
  obligated: BreakdownSplit<T>;
  exceeded: BreakdownSplit<T>;
};

export type Breakdowns<T = MonetaryValue> = BudgetTree<Breakdown<T>>;

type ApiBreakdown = Breakdown<string>;
type ApiBreakdowns = Breakdowns<string>;

const convertBreakdown = (breakdown: ApiBreakdown): Breakdown => ({
  allocated: fromString(breakdown.allocated),
  remaining: fromString(breakdown.remaining),
  obligated: {
    committed: fromString(breakdown.obligated.committed),
    used: fromString(breakdown.obligated.used),
  },
  exceeded: {
    committed: fromString(breakdown.exceeded.committed),
    used: fromString(breakdown.exceeded.used),
  },
});

const convertBreakdowns = (breakdowns: ApiBreakdowns): Breakdowns => {
  const convertBranch = <T extends undefined>(
    branch: BranchOf<ApiBreakdowns> | T,
  ): BranchOf<Breakdowns> | T =>
    branch
      ? {
          dimension: branch.dimension,
          nodes: branch.nodes.map((node) => ({
            values: node.values,
            outcome: convertBreakdown(node.outcome),
            branch: convertBranch(node.branch),
          })),
        }
      : branch;
  return {
    outcome: convertBreakdown(breakdowns.outcome),
    branch: convertBranch<never>(breakdowns.branch),
  };
};

export type BreakdownsResponse = {
  breakdowns: Breakdowns;
  viewPeriod: PeriodInterval;
  budgetPeriod: PeriodInterval;
};

export const useGetBudgetBreakdowns = (
  budgetDefinitionId: string,
  period: PeriodInterval,
): UseQueryResult<BreakdownsResponse> => {
  const company = useCompany();
  const viewPeriod = buildApiPeriod(period);
  return useQuery(
    getApiKey([budgetDefinitionId, 'breakdowns', viewPeriod]),
    async () => {
      const response =
        await getV2ApiCompanyIdBudgetDefinitionsBudgetDefinitionIdBreakdowns({
          path: {
            companyId: company.id,
            budgetDefinitionId,
          },
          query: {
            viewStart: viewPeriod.start,
            viewEnd: viewPeriod.end,
          },
        });

      if (response.statusCode !== 200) {
        throw new Error('Failed to get budget definitions');
      }
      return {
        breakdowns: convertBreakdowns(
          response.body.breakdowns as ApiBreakdowns,
        ),
        viewPeriod: buildPeriod(response.body.viewPeriod),
        budgetPeriod: buildPeriod(response.body.budgetPeriod),
      };
    },
    {
      retry: false,
    },
  );
};
